import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import type { StatementConfigDTO } from '@/app/(dashboard)/master/statement/apis/use-get-statement-configs';
import { statementConfigKeys } from './keys';

export const statementConfigUrls = {
  mine: '/api/v1/ceisa/uploads/statement-configs/mine',
  setDefault: (id: number) => `/api/v1/ceisa/uploads/statement-configs/${id}/default`,
};

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/** GET /statement-configs/mine: the caller's PPJK active configs, Utama first. Throws on failure. */
export const fetchMyStatementConfigs = async (signal?: AbortSignal): Promise<StatementConfigDTO[]> => {
  const response = await axiosInstance.get<{ success?: boolean; data?: StatementConfigDTO[] }>(
    statementConfigUrls.mine,
    { signal }
  );
  const rows = response.data?.data;
  return Array.isArray(rows) ? rows.filter((row) => row.isActive !== false) : [];
};

/**
 * The caller's PPJK active Pernyataan configs, Utama first. The one reader of
 * `/mine` (draft switcher and onboarding wizard). A 404 (a backend without
 * the route) is an empty list; any other failure stays an error, so a 401 or
 * a network blip is not cached as "no configs" and the next read tries again.
 * Callers treat an error like an empty list (the switcher hides).
 */
export const useMyStatementConfigs = (enabled = true) =>
  useQuery({
    queryKey: statementConfigKeys.mine(),
    enabled,
    staleTime: 3 * 60 * 1000,
    retry: (failureCount, error) => ![401, 403, 404].includes(statusOf(error) ?? 0) && failureCount < 1,
    queryFn: async ({ signal }): Promise<StatementConfigDTO[]> => {
      try {
        return await fetchMyStatementConfigs(signal);
      } catch (error) {
        if (statusOf(error) === 404) return [];
        throw error;
      }
    },
  });

/** Makes one active row the PPJK's Utama. */
export const setDefaultStatementConfig = async (id: number): Promise<void> => {
  await axiosInstance.put(statementConfigUrls.setDefault(id));
};

/** One read of the Master list, for code paths that need the rows right away. */
export const fetchStatementConfigs = async (ppjkNpwp?: string): Promise<StatementConfigDTO[]> => {
  const query = ppjkNpwp ? `?ppjkNpwp=${encodeURIComponent(ppjkNpwp)}` : '';
  const response = await axiosInstance.get<{ data?: StatementConfigDTO[] }>(
    `/api/v1/ceisa/uploads/statement-configs${query}`
  );
  return Array.isArray(response.data?.data) ? response.data.data : [];
};
